import type { FramingBox, FramingSceneInspector } from "../framingInspector";
import { cropWindowAtTime } from "../framingOverlayMath";

/**
 * Privacy-safe subject-verdict chain (CROP-WB-008):
 *
 *   targeted -> observed -> protected -> inside plan -> inside applied -> delivered
 *
 * Every step carries its evidence source. Steps are only computed when the
 * geometry shares a valid coordinate root; anything else is not comparable
 * and stays not_evaluated/unavailable instead of being invented.
 */

export type FramingVerdictStep =
  | "targeted"
  | "observed"
  | "protected"
  | "inside_plan"
  | "inside_applied"
  | "delivered";

export const FRAMING_VERDICT_STEPS: readonly FramingVerdictStep[] = [
  "targeted",
  "observed",
  "protected",
  "inside_plan",
  "inside_applied",
  "delivered",
];

export type FramingVerdictValue =
  | "yes"
  | "no"
  | "not_evaluated"
  | "ambiguous"
  | "unavailable";

export type FramingVerdictCell = {
  value: FramingVerdictValue;
  evidence: string;
};

export type FramingSubjectVerdictRow = {
  subjectId: string;
  kind: string;
  priority: string | null;
  identityRequired: boolean;
  identityUnverified: boolean;
  steps: Record<FramingVerdictStep, FramingVerdictCell>;
  visibleFraction: number | null;
  minEdgeMargin: number | null;
};

export function intersectionFraction(
  inner: FramingBox,
  outer: FramingBox,
): number {
  const innerArea =
    Math.max(0, inner.xMax - inner.xMin) * Math.max(0, inner.yMax - inner.yMin);
  if (innerArea <= 0) return 0;
  const width = Math.max(
    0,
    Math.min(inner.xMax, outer.xMax) - Math.max(inner.xMin, outer.xMin),
  );
  const height = Math.max(
    0,
    Math.min(inner.yMax, outer.yMax) - Math.max(inner.yMin, outer.yMin),
  );
  return (width * height) / innerArea;
}

export function boxIntersectionOverUnion(a: FramingBox, b: FramingBox): number {
  const width = Math.max(
    0,
    Math.min(a.xMax, b.xMax) - Math.max(a.xMin, b.xMin),
  );
  const height = Math.max(
    0,
    Math.min(a.yMax, b.yMax) - Math.max(a.yMin, b.yMin),
  );
  const intersection = width * height;
  const areaA = Math.max(0, a.xMax - a.xMin) * Math.max(0, a.yMax - a.yMin);
  const areaB = Math.max(0, b.xMax - b.xMin) * Math.max(0, b.yMax - b.yMin);
  const union = areaA + areaB - intersection;
  return union <= 0 ? 0 : intersection / union;
}

/** Plan/applied delta is only meaningful when both share one valid root. */
export function planAppliedComparison(inspector: FramingSceneInspector): {
  comparable: boolean;
  reason: string | null;
  iou: number | null;
} {
  const planned = inspector.decision?.cropWindow ?? null;
  const applied = inspector.appliedReceipt?.cropWindow ?? null;
  if (!planned || !applied) {
    return {
      comparable: false,
      reason: "Planned or applied crop window is unavailable.",
      iou: null,
    };
  }
  const planSpace = inspector.decision?.coordinateSpaceId ?? null;
  const receiptSpace =
    inspector.appliedReceipt?.sourceCoordinateSpaceId ?? null;
  if (planSpace && receiptSpace && planSpace !== receiptSpace) {
    return {
      comparable: false,
      reason: "Planned and applied windows live in different coordinate roots.",
      iou: null,
    };
  }
  return {
    comparable: true,
    reason: null,
    iou: boxIntersectionOverUnion(planned, applied),
  };
}

const VISIBLE_THRESHOLD = 0.985;

export function buildSubjectVerdicts(
  inspector: FramingSceneInspector,
): FramingSubjectVerdictRow[] {
  const subjects = inspector.intent?.subjects ?? [];
  const rows: FramingSubjectVerdictRow[] = [];
  for (const subject of subjects) {
    const subjectId = subject.subjectId ?? null;
    if (!subjectId) continue;
    const observations = inspector.observations.filter(
      (observation) => observation.subjectId === subjectId,
    );
    const observed = observations.length > 0;
    const protectedIds = inspector.decision?.protectedSubjectIds ?? [];
    const sacrificedIds =
      inspector.decision?.sacrificedPreferredSubjectIds ?? [];

    const steps = {} as Record<FramingVerdictStep, FramingVerdictCell>;
    steps.targeted = {
      value: "yes",
      evidence: `${subject.intentSource ?? inspector.intent?.source ?? "intent"}`,
    };
    steps.observed = observed
      ? {
          value: "yes",
          evidence: [
            ...new Set(
              observations.map(
                (observation) => observation.detector ?? "detector",
              ),
            ),
          ].join(", "),
        }
      : { value: "no", evidence: "no detector observation" };

    steps.protected = !inspector.decision
      ? { value: "unavailable", evidence: "no framing decision recorded" }
      : protectedIds.includes(subjectId)
        ? { value: "yes", evidence: "decision.protected_subject_ids" }
        : sacrificedIds.includes(subjectId)
          ? {
              value: "no",
              evidence: "decision.sacrificed_preferred_subject_ids",
            }
          : observed
            ? { value: "ambiguous", evidence: "not named by the decision" }
            : { value: "not_evaluated", evidence: "nothing to protect" };

    steps.inside_plan = containmentStep(
      observations,
      inspector.decision?.cropWindow ?? null,
      inspector.decision?.coordinateSpaceId ?? null,
      "planned crop window",
    );
    const receipt = inspector.appliedReceipt;
    const appliedWindow =
      receipt == null
        ? null
        : receipt.cropPath.length > 0
          ? cropWindowAtTime(receipt.cropPath, 0)
          : (receipt.cropWindow ?? null);
    steps.inside_applied = containmentStep(
      observations,
      appliedWindow,
      receipt?.sourceCoordinateSpaceId ?? null,
      "applied crop window",
    );

    const delivered = inspector.qcHistory.flatMap((qc) =>
      qc.deliveredSubjectEvidence.filter(
        (evidence) => evidence.subjectId === subjectId,
      ),
    );
    if (delivered.length > 0) {
      const matched = delivered.filter(
        (evidence) => evidence.matchStatus === "matched",
      );
      const wrong = delivered.filter(
        (evidence) => evidence.matchStatus === "wrong_subject",
      );
      steps.delivered =
        matched.length > 0 && wrong.length === 0
          ? {
              value: "yes",
              evidence: `${matched.length} delivered sample${matched.length === 1 ? "" : "s"}`,
            }
          : wrong.length > 0 && matched.length === 0
            ? { value: "no", evidence: "wrong-subject delivered evidence" }
            : { value: "ambiguous", evidence: "mixed delivered evidence" };
    } else {
      const semanticStatus = inspector.qcHistory.at(-1)?.semanticVerdict;
      steps.delivered =
        semanticStatus === "missing"
          ? { value: "no", evidence: "delivered semantic verdict: missing" }
          : {
              value: observed ? "not_evaluated" : "unavailable",
              evidence: "no delivered-pixel evidence for this subject",
            };
    }

    const visibleFraction = numericMetric(inspector, "min_retained_ratio");
    const minEdgeMargin = numericMetric(inspector, "min_edge_margin");
    const identityUnverified = observations.some(
      (observation) =>
        observation.identityStatus === "unverified" ||
        observation.identityStatus === "unknown",
    );
    rows.push({
      subjectId,
      kind: subject.kind ?? "unknown",
      priority: subject.priority ?? null,
      identityRequired: subject.identityRequired === true,
      identityUnverified,
      steps,
      visibleFraction,
      minEdgeMargin,
    });
  }
  return rows;
}

function containmentStep(
  observations: FramingSceneInspector["observations"],
  window: FramingBox | null,
  windowSpaceId: string | null,
  evidenceLabel: string,
): FramingVerdictCell {
  if (!window) {
    return { value: "unavailable", evidence: `${evidenceLabel} not recorded` };
  }
  if (observations.length === 0) {
    return { value: "not_evaluated", evidence: "no observation to test" };
  }
  const comparable = observations.filter(
    (observation) =>
      windowSpaceId == null || observation.coordinateSpaceId === windowSpaceId,
  );
  if (comparable.length === 0) {
    // Geometry lives in a different root; never project across it.
    return {
      value: "not_evaluated",
      evidence: "observation and window are in different coordinate roots",
    };
  }
  const fractions = comparable.map((observation) =>
    intersectionFraction(observation.bbox, window),
  );
  const worst = Math.min(...fractions);
  if (worst >= VISIBLE_THRESHOLD) {
    return {
      value: "yes",
      evidence: `${evidenceLabel}, ${Math.round(worst * 100)}% inside`,
    };
  }
  return {
    value: "no",
    evidence: `${evidenceLabel}, only ${Math.round(worst * 100)}% inside`,
  };
}

function numericMetric(
  inspector: FramingSceneInspector,
  key: string,
): number | null {
  for (const qc of [...inspector.qcHistory].reverse()) {
    const value = qc.metrics?.[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}
